import React from 'react';
import { Mail, ShieldCheck, Landmark, Target, TrendingUp, FileText, ArrowRight } from 'lucide-react';
import Reveal from '../components/Reveal';
import PageHeader from '../components/PageHeader';
import { images, company, investor } from '../mock';

const strategyIcons = [Target, TrendingUp, Landmark];

const Investors = () => {
  return (
    <div>
      <PageHeader
        eyebrow="Investors"
        title="An American Magnesium Solution"
        subtitle={investor.intro}
        image={images.projectWhitemoon}
      />

      {/* THE MARKET */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow text-amber-700 mb-4">The Market</div>
            <h2 className="font-display text-3xl md:text-4xl font-medium text-neutral-900 max-w-2xl leading-tight">
              A critical mineral with a fragile, concentrated supply chain.
            </h2>
          </Reveal>
          <div className="mt-16 grid md:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
            {investor.market.map((m, i) => (
              <Reveal key={i} delay={i * 90} className="bg-white">
                <div className="p-8">
                  <div className="font-display text-4xl md:text-5xl font-bold text-neutral-900">{m.value}</div>
                  <div className="mt-4 text-neutral-600 leading-relaxed text-sm">{m.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120}>
            <p className="mt-8 text-neutral-500 text-sm leading-relaxed max-w-3xl">{investor.marketNote}</p>
          </Reveal>
        </div>
      </section>

      {/* THE ASSET */}
      <section className="py-28 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4">
            <Reveal>
              <ShieldCheck className="w-8 h-8 text-amber-700 mb-5" strokeWidth={1.5} />
              <div className="eyebrow text-amber-700 mb-4">The Asset</div>
              <h2 className="font-display text-3xl md:text-4xl font-medium text-neutral-900 leading-tight">
                Owned outright, with a recognized right to mine.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-8 space-y-8">
            {investor.asset.map((a, i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="border-t border-neutral-200 pt-6">
                  <h3 className="font-display text-xl font-semibold text-neutral-900 mb-3">{a.title}</h3>
                  <p className="text-neutral-700 leading-relaxed text-lg">{a.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STRATEGY */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow text-amber-700 mb-4">The Strategy</div>
            <h2 className="font-display text-3xl md:text-4xl font-medium text-neutral-900">Revenue before scale.</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {investor.strategy.map((s, i) => {
              const Icon = strategyIcons[i % strategyIcons.length];
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="h-full bg-stone-50 border border-neutral-200 p-8">
                    <div className="flex items-center gap-3 mb-5">
                      <Icon className="w-6 h-6 text-amber-700" strokeWidth={1.5} />
                      <span className="eyebrow text-neutral-400 text-[0.6rem]">0{i + 1}</span>
                    </div>
                    <h3 className="font-display text-2xl font-semibold text-neutral-900 mb-3">{s.step}</h3>
                    <p className="text-neutral-600 leading-relaxed">{s.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* VALIDATION */}
      <section className="py-28 bg-neutral-950 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="eyebrow text-amber-500 mb-4">Commercial & Defense Validation</div>
            <h2 className="font-display text-3xl md:text-4xl font-medium max-w-2xl leading-tight">
              Demand, under letters of intent.
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {investor.validation.map((v, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="border-t border-neutral-800 pt-6">
                  <div className="flex flex-wrap items-baseline gap-3">
                    <span className="font-display text-5xl font-bold text-amber-500">{v.count}</span>
                    <span className="eyebrow text-neutral-500 text-[0.6rem]">{v.note}</span>
                  </div>
                  <div className="mt-3 text-neutral-200 text-lg">{v.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={120}>
            <p className="mt-12 text-neutral-400 leading-relaxed max-w-3xl">{investor.validationNote}</p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <FileText className="w-8 h-8 text-amber-700 mx-auto mb-6" strokeWidth={1.5} />
            <h2 className="font-display text-3xl md:text-4xl font-medium text-neutral-900 leading-tight">
              For investors.
            </h2>
            <p className="mt-6 text-neutral-600 leading-relaxed">
              Whitemoon is advancing a staged development program. For more information, including our full investor
              presentation and supporting technical materials, please reach out and we will follow up directly with
              qualified parties.
            </p>
            <a
              href={`mailto:${company.email}`}
              className="mt-10 inline-flex flex-wrap items-center justify-center gap-2 max-w-full bg-neutral-900 text-white px-6 sm:px-10 py-4 eyebrow hover:bg-amber-700 transition-colors duration-300"
            >
              {company.email} <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="bg-stone-50 border-t border-neutral-200 py-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="eyebrow text-neutral-400 mb-4 flex items-center gap-2">
            <Mail className="w-3.5 h-3.5" /> Important Information
          </div>
          <p className="text-neutral-500 text-xs leading-relaxed">{investor.disclaimer}</p>
        </div>
      </section>
    </div>
  );
};

export default Investors;
